import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Col,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
} from "reactstrap"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import { SelectWithOthers } from "components/SelectWithOthers"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient from "helpers/apiClient"

const purposeOptions = [
  { value: "Buy", label: "Buy" },
  { value: "Rent", label: "Rent" },
  { value: "Invest", label: "Invest" },
]
const propertyTypeOptions = [
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
]
const leadSourceOptions = [
  { value: "Website", label: "Website" },
  { value: "Portal", label: "Portal" },
  { value: "Walk-in", label: "Walk-in" },
  { value: "Referral", label: "Referral" },
]
const inventorySourceOptions = [
  { value: "soldx", label: "SoldX Inventory - Pick from existing SoldX listed properties" },
  { value: "new", label: "Create New Property - Add new property manually" },
]
const newPropertyTypeOptions = [
  { value: "Flat", label: "Flat" },
  { value: "Office", label: "Office" },
  { value: "Plot", label: "Plot" },
]
const availabilityOptions = [
  { value: "Available", label: "Available" },
  { value: "Sold", label: "Sold" },
]
const yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
]
const legalStatusOptions = [
  { value: "Approved", label: "Approved" },
  { value: "Pending", label: "Pending" },
]
const buyerTypeOptions = [
  { value: "End-user", label: "End-user" },
  { value: "Investor", label: "Investor" },
]
const convertToLeadOptions = [
  { value: "Yes", label: "Yes - If client looks for new property" },
  { value: "No", label: "No" },
]
const brokerStatusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
]
const transactionStageOptions = [
  { value: "Initiated", label: "Initiated" },
  { value: "Negotiation", label: "Negotiation" },
  { value: "Booking Done", label: "Booking Done" },
  { value: "Agreement Signed", label: "Agreement Signed" },
  { value: "Registration Done", label: "Registration Done" },
  { value: "Completed", label: "Completed" },
]
const transactionStatusOptions = [
  { value: "Active", label: "Active" },
  { value: "Completed", label: "Completed" },
]
const activityTypeOptions = [
  { value: "Call", label: "Call" },
  { value: "Visit", label: "Visit" },
  { value: "Meeting", label: "Meeting" },
]
const outcomeOptions = [
  { value: "Positive", label: "Positive" },
  { value: "Pending", label: "Pending" },
]

interface BreadcrumbItem {
    title: string
    link: string
}

const FORM_KEYS = [
    "leadName", "mobile", "email", "budget", "purpose", "propertyType", "preferredLocation", "leadSource", "followUpDate", "notes",
    "inventorySource", "projectName", "developerName", "newPropertyType", "unitNumber", "floor", "carpetArea", "builtUpArea", "superArea", "facing",
    "price", "ratePerSqft", "availabilityStatus", "possessionDate", "amenities", "parking", "legalStatus",
    "clientName", "clientMobile", "clientEmail", "address", "buyerType", "pastTransactions", "clientNotes", "convertToLead",
    "brokerName", "companyName", "contactPerson", "brokerMobile", "brokerEmail", "city", "projectsAssigned", "brokerStatus", "leadsAssigned", "dealsClosed", "revenueGenerated",
    "seller", "buyer", "property", "transactionValue", "bookingAmount", "transactionStage", "transactionDate", "transactionStatus",
    "activityType", "linkedLeadClient", "activityDateTime", "outcome", "nextActionDate", "activityNotes", "geotag",
    "documentProperty", "documentName", "documentNotes",
]

interface CrmProps {
    setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const breadcrumbItems: BreadcrumbItem[] = [
    { title: "CRM", link: "#" },
    { title: "CRM Form", link: "#" },
]

const Crm = (props: CrmProps) => {
    const navigate = useNavigate()
    const { id: editId } = useParams<{ id?: string }>()
    useEffect(() => {
        props.setBreadcrumbItems("CRM", breadcrumbItems)
    }, [props])

    useEffect(() => {
        if (!editId) return
        setLoadLoading(true)
        setLoadError(null)
        apiClient.get(`/api/crm/getCrmById/${editId}`)
            .then((res) => {
                const data = res?.data?.data ?? res?.data
                if (data && typeof data === "object") {
                    const next: Record<string, string | string[]> = {}
                    FORM_KEYS.forEach((key) => {
                        const val = data[key]
                        if (val != null && val !== undefined) next[key] = Array.isArray(val) ? val : String(val)
                    })
                    setForm((prev) => ({ ...prev, ...next }))
                    const setCheck = (id: string, v: boolean) => {
                        const el = document.getElementById(id) as HTMLInputElement | null
                        if (el) el.checked = !!v
                    }
                    setCheck("whatsapp", data.whatsappIntegration)
                    setCheck("emailTemplates", data.emailTemplates)
                    setCheck("smsAlerts", data.smsAlerts)
                    setCheck("reminderAutomation", data.reminderAutomation)
                }
            })
            .catch((err) => setLoadError(err?.response?.data?.message || err?.message || "Failed to load CRM"))
            .finally(() => setLoadLoading(false))
    }, [editId])

    const [submitLoading, setSubmitLoading] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [loadLoading, setLoadLoading] = useState(false)

    const [form, setForm] = useState<Record<string, string | string[]>>({
        leadName: "",
        mobile: "",
        email: "",
        budget: "",
        purpose: "",
        propertyType: "",
        preferredLocation: "",
        leadSource: "",
        followUpDate: "",
        notes: "",
        inventorySource: "",
        projectName: "",
        developerName: "",
        newPropertyType: "",
        unitNumber: "",
        floor: "",
        carpetArea: "",
        builtUpArea: "",
        superArea: "",
        facing: "",
        price: "",
        ratePerSqft: "",
        availabilityStatus: "",
        possessionDate: "",
        amenities: "",
        parking: "",
        legalStatus: "",
        clientName: "",
        clientMobile: "",
        clientEmail: "",
        address: "",
        buyerType: "",
        pastTransactions: "",
        clientNotes: "",
        convertToLead: "",
        brokerName: "",
        companyName: "",
        contactPerson: "",
        brokerMobile: "",
        brokerEmail: "",
        city: "",
        projectsAssigned: "",
        brokerStatus: "",
        leadsAssigned: "",
        dealsClosed: "",
        revenueGenerated: "",
        seller: "",
        buyer: "",
        property: "",
        transactionValue: "",
        bookingAmount: "",
        transactionStage: "",
        transactionDate: "",
        transactionStatus: "",
        activityType: "",
        linkedLeadClient: "",
        activityDateTime: "",
        outcome: "",
        nextActionDate: "",
        activityNotes: "",
        geotag: "",
        documentProperty: "",
        documentName: "",
        documentNotes: "",
    })

    const update = (name: string, value: string | string[]) => {
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSubmitError(null)
        setSubmitSuccess(null)
        setSubmitLoading(true)
        try {
            const formData = new FormData()
            Object.entries(form).forEach(([key, value]) => {
                if (value !== "" && value != null && value !== undefined) {
                    formData.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value))
                }
            })
            const whatsappEl = document.getElementById("whatsapp") as HTMLInputElement | null
            const emailTemplatesEl = document.getElementById("emailTemplates") as HTMLInputElement | null
            const smsAlertsEl = document.getElementById("smsAlerts") as HTMLInputElement | null
            const reminderEl = document.getElementById("reminderAutomation") as HTMLInputElement | null
            formData.append("whatsappIntegration", String(whatsappEl?.checked ?? false))
            formData.append("emailTemplates", String(emailTemplatesEl?.checked ?? false))
            formData.append("smsAlerts", String(smsAlertsEl?.checked ?? false))
            formData.append("reminderAutomation", String(reminderEl?.checked ?? false))
            if (editId) formData.append("id", editId)
            const propertyMediaInput = document.querySelector<HTMLInputElement>('input[name="propertyMedia"]')
            if (propertyMediaInput?.files?.length) {
                ;[...propertyMediaInput.files].forEach((f) => formData.append("propertyMedia", f))
            }
            const docInput = document.querySelector<HTMLInputElement>('input[name="documentFile"]')
            if (docInput?.files?.[0]) formData.append("documentFile", docInput.files[0])
            const res = await apiClient.post("/api/crm/saveCrm", formData)
            const data = res?.data ?? res
            const msg = (data as any)?.message || "CRM saved successfully."
            setSubmitSuccess(msg)
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Failed to save CRM"
            setSubmitError(msg)
        } finally {
            setSubmitLoading(false)
        }
    }

    return (
        <React.Fragment>
            <Row>
                <Col xs="12">
                    {loadLoading && editId && <Alert color="info">Loading CRM...</Alert>}
                    {loadError && <Alert color="danger">{loadError}</Alert>}
                    {submitSuccess && <Alert color="success">{submitSuccess}</Alert>}
                    {submitError && <Alert color="danger">{submitError}</Alert>}
                        <Form onSubmit={handleSubmit}>
                        {/* LEAD MASTER */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Lead Master</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Lead Name</Label>
                                            <Input type="text" placeholder="Prospect full name" value={form.leadName as string} onChange={(e) => update("leadName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Mobile</Label>
                                            <Input type="text" placeholder="Primary contact number" value={form.mobile as string} onChange={(e) => update("mobile", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Email</Label>
                                            <Input type="email" placeholder="Email ID" value={form.email as string} onChange={(e) => update("email", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Budget</Label>
                                            <Input type="text" placeholder="Client budget" value={form.budget as string} onChange={(e) => update("budget", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Purpose</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.purpose as string}
                                                onChange={(v) => update("purpose", v)}
                                                options={purposeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Property Type</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.propertyType as string}
                                                onChange={(v) => update("propertyType", v)}
                                                options={propertyTypeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Preferred Location</Label>
                                            <Input type="text" placeholder="Area / City" value={form.preferredLocation as string} onChange={(e) => update("preferredLocation", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Lead Source</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.leadSource as string}
                                                onChange={(v) => update("leadSource", v)}
                                                options={leadSourceOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Follow-up Date</Label>
                                            <Flatpickr
                                                className="form-control d-block"
                                                placeholder="dd M, yyyy"
                                                value={form.followUpDate as string || undefined}
                                                onChange={(_dates, dateStr) => update("followUpDate", dateStr || "")}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "F j, Y",
                                                    dateFormat: "Y-m-d",
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Notes</Label>
                                            <Input type="textarea" rows={2} placeholder="Internal remarks" value={form.notes as string} onChange={(e) => update("notes", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* INVENTORY SOURCE */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Inventory Source</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Source</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.inventorySource as string}
                                                onChange={(v) => update("inventorySource", v)}
                                                options={inventorySourceOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* NEW PROPERTY (IF CREATED) */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">New Property (if created)</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Project Name</Label>
                                            <Input type="text" placeholder="Name of project" value={form.projectName as string} onChange={(e) => update("projectName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Developer Name</Label>
                                            <Input type="text" placeholder="Builder / Owner" value={form.developerName as string} onChange={(e) => update("developerName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Property Type</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.newPropertyType as string}
                                                onChange={(v) => update("newPropertyType", v)}
                                                options={newPropertyTypeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Unit Number</Label>
                                            <Input type="text" placeholder="Unit identifier" value={form.unitNumber as string} onChange={(e) => update("unitNumber", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Floor</Label>
                                            <Input type="text" placeholder="Floor number" value={form.floor as string} onChange={(e) => update("floor", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Carpet Area (sqft)</Label>
                                            <Input type="text" placeholder="Carpet area" value={form.carpetArea as string} onChange={(e) => update("carpetArea", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Built-up Area (sqft)</Label>
                                            <Input type="text" placeholder="Built-up area" value={form.builtUpArea as string} onChange={(e) => update("builtUpArea", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Super Area (sqft)</Label>
                                            <Input type="text" placeholder="Super area" value={form.superArea as string} onChange={(e) => update("superArea", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Facing</Label>
                                            <Input type="text" placeholder="Direction" value={form.facing as string} onChange={(e) => update("facing", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Price</Label>
                                            <Input type="text" placeholder="Total price" value={form.price as string} onChange={(e) => update("price", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Rate per Sqft</Label>
                                            <Input type="text" placeholder="Rate" value={form.ratePerSqft as string} onChange={(e) => update("ratePerSqft", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Availability Status</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.availabilityStatus as string}
                                                onChange={(v) => update("availabilityStatus", v)}
                                                options={availabilityOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Possession Date</Label>
                                            <Flatpickr
                                                className="form-control d-block"
                                                placeholder="dd M, yyyy"
                                                value={form.possessionDate as string || undefined}
                                                onChange={(_dates, dateStr) => update("possessionDate", dateStr || "")}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "F j, Y",
                                                    dateFormat: "Y-m-d",
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Amenities</Label>
                                            <Input type="textarea" rows={2} placeholder="List of amenities" value={form.amenities as string} onChange={(e) => update("amenities", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Parking</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.parking as string}
                                                onChange={(v) => update("parking", v)}
                                                options={yesNoOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Legal Status</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.legalStatus as string}
                                                onChange={(v) => update("legalStatus", v)}
                                                options={legalStatusOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Media (Images / Videos)</Label>
                                            <Input type="file" name="propertyMedia" multiple accept="image/*,video/*" />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* CLIENT / CUSTOMER */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Client / Customer (Auto-created)</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Client Name</Label>
                                            <Input type="text" placeholder="Customer name" value={form.clientName as string} onChange={(e) => update("clientName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Mobile</Label>
                                            <Input type="text" placeholder="Contact number" value={form.clientMobile as string} onChange={(e) => update("clientMobile", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Email</Label>
                                            <Input type="email" placeholder="Email ID" value={form.clientEmail as string} onChange={(e) => update("clientEmail", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Address</Label>
                                            <Input type="text" placeholder="Residential / Office address" value={form.address as string} onChange={(e) => update("address", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Buyer Type</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.buyerType as string}
                                                onChange={(v) => update("buyerType", v)}
                                                options={buyerTypeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Convert to Lead</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.convertToLead as string}
                                                onChange={(v) => update("convertToLead", v)}
                                                options={convertToLeadOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Past Transactions</Label>
                                            <Input type="textarea" rows={2} placeholder="Transaction history" value={form.pastTransactions as string} onChange={(e) => update("pastTransactions", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Notes</Label>
                                            <Input type="textarea" rows={2} placeholder="Remarks" value={form.clientNotes as string} onChange={(e) => update("clientNotes", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* BROKER / CHANNEL PARTNER */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Broker / Channel Partner</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Broker Name</Label>
                                            <Input type="text" placeholder="Individual / Firm" value={form.brokerName as string} onChange={(e) => update("brokerName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Company Name</Label>
                                            <Input type="text" placeholder="Brokerage firm" value={form.companyName as string} onChange={(e) => update("companyName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Contact Person</Label>
                                            <Input type="text" placeholder="Primary contact" value={form.contactPerson as string} onChange={(e) => update("contactPerson", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Mobile</Label>
                                            <Input type="text" placeholder="Phone number" value={form.brokerMobile as string} onChange={(e) => update("brokerMobile", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Email</Label>
                                            <Input type="email" placeholder="Email ID" value={form.brokerEmail as string} onChange={(e) => update("brokerEmail", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>City</Label>
                                            <Input type="text" placeholder="Operating city" value={form.city as string} onChange={(e) => update("city", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Status</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.brokerStatus as string}
                                                onChange={(v) => update("brokerStatus", v)}
                                                options={brokerStatusOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Projects Assigned</Label>
                                            <Input type="text" placeholder="Linked projects" value={form.projectsAssigned as string} onChange={(e) => update("projectsAssigned", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Leads Assigned</Label>
                                            <Input type="text" placeholder="Number of leads" value={form.leadsAssigned as string} onChange={(e) => update("leadsAssigned", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Deals Closed</Label>
                                            <Input type="text" placeholder="Number of transactions" value={form.dealsClosed as string} onChange={(e) => update("dealsClosed", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Revenue Generated</Label>
                                            <Input type="text" placeholder="Total value" value={form.revenueGenerated as string} onChange={(e) => update("revenueGenerated", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* TRANSACTION MANAGEMENT */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Transaction Management</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Seller</Label>
                                            <SelectWithOthers
                                                placeholder="Pick from Lead"
                                                value={form.seller as string}
                                                onChange={(v) => update("seller", v)}
                                                options={[]}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Buyer</Label>
                                            <SelectWithOthers
                                                placeholder="Pick from Lead / Broker"
                                                value={form.buyer as string}
                                                onChange={(v) => update("buyer", v)}
                                                options={[]}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Property</Label>
                                            <SelectWithOthers
                                                placeholder="Pick from Inventory"
                                                value={form.property as string}
                                                onChange={(v) => update("property", v)}
                                                options={[]}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Transaction Stage</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.transactionStage as string}
                                                onChange={(v) => update("transactionStage", v)}
                                                options={transactionStageOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Transaction Value</Label>
                                            <Input type="text" placeholder="Final amount" value={form.transactionValue as string} onChange={(e) => update("transactionValue", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Booking Amount</Label>
                                            <Input type="text" placeholder="Initial amount" value={form.bookingAmount as string} onChange={(e) => update("bookingAmount", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Transaction Date</Label>
                                            <Flatpickr
                                                className="form-control d-block"
                                                placeholder="dd M, yyyy"
                                                value={form.transactionDate as string || undefined}
                                                onChange={(_dates, dateStr) => update("transactionDate", dateStr || "")}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "F j, Y",
                                                    dateFormat: "Y-m-d",
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Status</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.transactionStatus as string}
                                                onChange={(v) => update("transactionStatus", v)}
                                                options={transactionStatusOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* SITE VISIT & ACTIVITY */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Site Visit & Activity</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Activity Type</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.activityType as string}
                                                onChange={(v) => update("activityType", v)}
                                                options={activityTypeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Linked Lead / Client</Label>
                                            <Input type="text" placeholder="Reference" value={form.linkedLeadClient as string} onChange={(e) => update("linkedLeadClient", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Date & Time</Label>
                                            <Flatpickr
                                                className="form-control d-block"
                                                placeholder="dd M, yyyy h:i K"
                                                value={form.activityDateTime as string || undefined}
                                                onChange={(_dates, dateStr) => update("activityDateTime", dateStr || "")}
                                                options={{
                                                    enableTime: true,
                                                    altInput: true,
                                                    altFormat: "F j, Y h:i K",
                                                    dateFormat: "Y-m-d H:i",
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Outcome</Label>
                                            <SelectWithOthers
                                                placeholder="Select"
                                                value={form.outcome as string}
                                                onChange={(v) => update("outcome", v)}
                                                options={outcomeOptions}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Next Action Date</Label>
                                            <Flatpickr
                                                className="form-control d-block"
                                                placeholder="Follow-up (dd M, yyyy)"
                                                value={form.nextActionDate as string || undefined}
                                                onChange={(_dates, dateStr) => update("nextActionDate", dateStr || "")}
                                                options={{
                                                    altInput: true,
                                                    altFormat: "F j, Y",
                                                    dateFormat: "Y-m-d",
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Geo-tag</Label>
                                            <Input type="text" placeholder="Site visit location" value={form.geotag as string} onChange={(e) => update("geotag", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Notes</Label>
                                            <Input type="textarea" rows={2} placeholder="Remarks" value={form.activityNotes as string} onChange={(e) => update("activityNotes", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* COMMUNICATION & AUTOMATION */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Communication & Automation</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup check>
                                            <Input type="checkbox" id="whatsapp" />
                                            <Label check for="whatsapp">WhatsApp Integration - Lead communication</Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup check>
                                            <Input type="checkbox" id="emailTemplates" />
                                            <Label check for="emailTemplates">Email Templates - Automated emails</Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup check>
                                            <Input type="checkbox" id="smsAlerts" />
                                            <Label check for="smsAlerts">SMS Alerts - Follow-ups</Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup check>
                                            <Input type="checkbox" id="reminderAutomation" />
                                            <Label check for="reminderAutomation">Reminder Automation - Task reminders</Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* DOCUMENT MANAGEMENT */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Document Management</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Property</Label>
                                            <SelectWithOthers
                                                placeholder="Select property"
                                                value={form.documentProperty as string}
                                                onChange={(v) => update("documentProperty", v)}
                                                options={[]}
                                                classNamePrefix="select2-selection"
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Document Name</Label>
                                            <Input type="text" placeholder="Custom name" value={form.documentName as string} onChange={(e) => update("documentName", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Document File</Label>
                                            <Input type="file" name="documentFile" />
                                            <small className="text-muted">Uploaded Date - Auto</small>
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label>Notes</Label>
                                            <Input type="textarea" rows={2} placeholder="Optional" value={form.documentNotes as string} onChange={(e) => update("documentNotes", e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* DASHBOARD & ANALYTICS - Info card */}
                        <Card className="mb-3">
                            <CardHeader>
                                <CardTitle tag="h5" className="mb-0">Dashboard & Analytics</CardTitle>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-0">
                                    View reports: Leads (source & conversion), Transactions (volume & value), Brokers (performance), Inventory (available vs sold), Revenue (monthly / project-wise).
                                </p>
                                <Button color="primary" className="mt-2">Open Analytics Dashboard</Button>
                            </CardBody>
                        </Card>

                        <div className="mb-4 d-flex justify-content-end gap-2">
                            <Button color="secondary" type="button" onClick={() => navigate("/crm")}>Cancel</Button>
                            <Button color="primary" type="submit" disabled={submitLoading}>{submitLoading ? "Saving..." : "Save"}</Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(Crm)
